<template>
  <component
    :is="componentTag"
    v-bind="componentAttrs"
    v-on="$listeners"
    class="cg-button"
    :class="{
      'cg-button--outlined': outlined,
      'cg-button--block': block,
      'cg-button--loading': loading,
    }"
    :disabled="disabled"
    :style="styleVariables"
    :aria-disabled="disabled ? 'true' : 'false'"
  >
    <div class="cg-button__content">
      <div v-if="showPrependIcon" class="cg-button__content-prepend-icon">
        <!-- @slot Adds an item before text. -->
        <slot name="prepend-icon" />
      </div>
      <!-- @slot The default Vue slot. -->
      <slot />

      <div v-if="showAppendIconSlot" class="cg-button__content-append-icon">
        <!-- @slot Adds an item after text. -->
        <slot name="append-icon" />
      </div>
    </div>

    <div v-if="loading" class="cg-button__loading">
      <div class="cg-button__loading-spinner" />
    </div>
  </component>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { Colors } from '../../utils/Colors';

type IButtonSlots = {
  'prepend-icon'?: unknown;
  'append-icon'?: unknown;
}

export default Vue.extend({
  name: 'CgButton',
  emits: [
    /**
     * Event that is emitted when the component is clicked.
     * 
     * @property {PointerEvent} event click event
     */
    'click',
  ],
  props: {
    /**
     * Makes the background transparent and applies a thin border.
     */
    outlined: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Expands the button to 100% of available space.
     */
    block: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Removes the ability to click or target the component.
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Adds a loading icon animation.
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Applies specified color for button background.
     */
    color: {
      type: String as PropType<Colors | String>,
      required: false,
      default: Colors.Black,
    },
    /**
     * Changes font size.
     */
    fontSize: {
      type: Number,
      required: false,
      default: 18,
    },
    /**
     * Applies specified color for button text. <br />
     * Not working with the **outlined** prop.
     */
    textColor: {
      type: String as PropType<Colors | String>,
      required: false,
      default: Colors.White,
    },
    /**
     * Changes line height.
     */
    lineHeight: {
      type: Number,
      required: false,
      default: 22,
    },
    /**
     * Changes font weight.
     */
    fontWeight: {
      type: Number,
      required: false,
      default: 400,
    },
    /**
     * Sets the height for the component.
     */
    height: {
      type: Number,
      required: false,
      default: 44,
    },
    /**
     * Render button as HTML anchor and applies the href attribute.
     */
    href: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * Render button as `router-link`. You can find more information about the [to prop](https://v3.router.vuejs.org/api/#to) on the vue-router documentation.
     */
    to: {
      type: [String, Object],
      required: false,
      default: null
    },
    /**
     * Specifies the link is a `nuxt-link`.
     */
    nuxt: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    styleVariables(): Object {
      const { color, height, fontSize, textColor, lineHeight, fontWeight } = this;

      return {
        '--button-color': color,
        '--button-height': `${height}px`,
        '--button-text-color': textColor,
        '--button-font-size': `${fontSize}px`,
        '--button-line-height': `${lineHeight}px`,
        '--button-font-weight': fontWeight,
      };
    },
    componentTag(): String {
      const { href, to, nuxt } = this;

      if (href) {
        return 'a';
      } else if (to) {
        return nuxt ? 'nuxt-link' : 'router-link';
      } else {
        return 'button';
      }
    },
    componentAttrs (): Record<string, string> {
      const { $attrs, href, to } = this;
      const result = { ...$attrs };

      if (href) { result['href'] = href; }
      if (to) { result['to'] = to; }
      
      return result;
    },
    slots(): IButtonSlots {
      return this.$slots;
    },
    showPrependIcon(): boolean {
      return !!this.slots?.['prepend-icon'];
    },
    showAppendIconSlot(): boolean {
      return !!this.slots?.['append-icon'];
    },
  },
});
</script>

<style lang="scss">
@import './styles.scss';
</style>
